import { createContext, createElement, useContext, type PropsWithChildren, type ReactElement } from "react";

import { createFetchClient, type VidarioClient } from "./endpoints";

const ClientContext = createContext<VidarioClient | null>(null);

export interface VidarioClientProviderProps extends PropsWithChildren {
  client: VidarioClient;
}

/** Lets consumers inject a custom VidarioClient (e.g. an MSW-backed one in
 * dev, a real openapi-fetch instance in prod, or a spy in tests). */
export function VidarioClientProvider({
  client,
  children,
}: VidarioClientProviderProps): ReactElement {
  return createElement(ClientContext.Provider, { value: client }, children);
}

export function useVidarioClient(): VidarioClient {
  const c = useContext(ClientContext);
  if (!c) {
    throw new Error("useVidarioClient must be used within <VidarioClientProvider>.");
  }
  return c;
}

/** Default client that talks to `${baseUrl}/...` using httpOnly cookies.
 * `mapKeys` controls camelCase↔snake_case translation — keep it on for the
 * real Django backend, off for the camelCase MSW mocks. */
export function defaultClient(
  baseUrl: string,
  mapKeys = true,
  onAuthExpired?: () => void,
): VidarioClient {
  return createFetchClient({ baseUrl, mapKeys, ...(onAuthExpired && { onAuthExpired }) });
}
